<template>
  <div class="product-catalogue">
    <div class="app-card catalogue-card">
      <div class="section-heading">
        <div>
          <h3>Add Product Catalogue</h3>
          <p>Select existing values or enter a new value at each level.</p>
        </div>
      </div>

      <el-form
        ref="catalogueForm"
        :model="form"
        :rules="rules"
        label-position="top"
      >
        <div class="catalogue-context">
          <el-form-item label="Manufacturer" prop="manufacturerId">
            <el-select
              v-model="form.manufacturerId"
              filterable
              placeholder="Select manufacturer"
              :loading="loadingManufacturers"
              @change="onManufacturerChange"
            >
              <el-option
                v-for="manufacturer in manufacturers"
                :key="manufacturer.id"
                :label="manufacturer.name"
                :value="manufacturer.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Brand" prop="brandValue">
            <el-autocomplete
              v-model="form.brandValue"
              clearable
              value-key="name"
              placeholder="Select or enter a brand"
              :disabled="!form.manufacturerId"
              :fetch-suggestions="fetchBrandSuggestions"
              :trigger-on-focus="true"
              @select="onBrandSelect"
              @change="onBrandChange"
            />
          </el-form-item>
        </div>

        <div v-if="form.brandValue" v-loading="loadingOptions">
          <div class="combinations-header">
            <div>
              <h4>Product combinations</h4>
              <span>Each card below will be saved as one product.</span>
            </div>
            <el-button
              type="primary"
              plain
              size="small"
              icon="el-icon-plus"
              @click="addProduct"
              >Add Combination</el-button
            >
          </div>

          <div
            v-for="(product, index) in form.products"
            :key="product.key"
            class="combination-card"
          >
            <div class="combination-title">
              <span>Combination {{ index + 1 }}</span>
              <el-button
                v-if="form.products.length > 1"
                type="text"
                class="remove-button"
                @click="removeProduct(index)"
                >Remove</el-button
              >
            </div>

            <div class="option-path">
              <el-form-item
                label="Product Type"
                :prop="`products.${index}.typeName`"
                :rules="requiredRule('Product type')"
              >
                <creatable-select
                  v-model="product.typeName"
                  :options="options.productTypes"
                  placeholder="Select or enter type"
                />
              </el-form-item>
              <el-form-item
                label="Product Name"
                :prop="`products.${index}.productName`"
                :rules="requiredRule('Product name')"
              >
                <creatable-select
                  v-model="product.productName"
                  :options="options.productNames"
                  placeholder="Select or enter name"
                />
              </el-form-item>
              <el-form-item label="Capacity">
                <creatable-select
                  v-model="product.capacityName"
                  :options="options.capacities"
                  placeholder="Select or enter capacity"
                />
              </el-form-item>
              <el-form-item label="Absorbency">
                <creatable-select
                  v-model="product.absorbencyName"
                  :options="options.absorbencies"
                  placeholder="Select or enter absorbency"
                />
              </el-form-item>
              <el-form-item label="Size">
                <creatable-select
                  v-model="product.sizeName"
                  :options="options.sizes"
                  placeholder="Select or enter size"
                />
              </el-form-item>
              <el-form-item label="Color">
                <creatable-select
                  v-model="product.colorName"
                  :options="options.colors"
                  placeholder="Select or enter color"
                />
              </el-form-item>
            </div>

            <div class="product-details">
              <el-form-item
                label="Product Number"
                :prop="`products.${index}.productNumber`"
                :rules="requiredRule('Product number')"
              >
                <el-input
                  v-model.trim="product.productNumber"
                  placeholder="e.g. TENA001"
                />
              </el-form-item>
              <el-form-item label="ISO Capacity">
                <el-input-number
                  v-model="product.isoCapacity"
                  :min="0"
                  :precision="0"
                  controls-position="right"
                  placeholder="e.g. 2000"
                />
              </el-form-item>
              <el-form-item label="Waist Size">
                <el-input
                  v-model.trim="product.waistSize"
                  placeholder="e.g. 56-91 cm"
                />
              </el-form-item>
            </div>
          </div>

          <div class="catalogue-actions">
            <el-button class="save-btn" :loading="saving" @click="saveCatalogue"
              >SAVE PRODUCT CATALOGUE</el-button
            >
          </div>
        </div>
      </el-form>
    </div>

    <div v-if="existingProducts.length" class="app-card existing-card">
      <div class="section-heading compact">
        <div>
          <h3>Existing products for {{ selectedBrandName }}</h3>
          <p>{{ existingProducts.length }} existing combinations</p>
        </div>
      </div>
      <el-table :data="existingProducts" border size="small" max-height="360">
        <el-table-column prop="typeName" label="Type" min-width="110" />
        <el-table-column
          prop="productName"
          label="Product Name"
          min-width="150"
        />
        <el-table-column prop="capacityName" label="Capacity" width="110" />
        <el-table-column prop="absorbencyName" label="Absorbency" width="115" />
        <el-table-column prop="sizeName" label="Size" width="100" />
        <el-table-column prop="colorName" label="Color" width="100" />
        <el-table-column
          prop="productNumber"
          label="Product Number"
          min-width="130"
        />
        <el-table-column prop="isoCapacity" label="ISO Capacity" width="115" />
        <el-table-column prop="waistSize" label="Waist Size" width="130" />
      </el-table>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/CommonApiService.js'

const CreatableSelect = {
  name: 'CreatableSelect',
  props: {
    value: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' }
  },
  render(h) {
    return h('el-autocomplete', {
      props: {
        value: this.value,
        fetchSuggestions: (query, callback) => {
          const normalized = String(query || '').toLowerCase()
          callback(
            this.options
              .filter(option => option.toLowerCase().includes(normalized))
              .map(option => ({ value: option }))
          )
        },
        clearable: true,
        placeholder: this.placeholder,
        triggerOnFocus: true
      },
      style: { width: '100%' },
      on: {
        input: value => this.$emit('input', value),
        select: item => this.$emit('input', item.value)
      }
    })
  }
}

const emptyOptions = () => ({
  productTypes: [],
  productNames: [],
  capacities: [],
  absorbencies: [],
  sizes: [],
  colors: []
})

let productKey = 0
const emptyProduct = () => ({
  key: ++productKey,
  typeName: '',
  productName: '',
  capacityName: '',
  absorbencyName: '',
  sizeName: '',
  colorName: '',
  productNumber: '',
  isoCapacity: undefined,
  waistSize: ''
})

export default {
  name: 'ProductCatalogue',
  components: { CreatableSelect },
  data() {
    return {
      manufacturers: [],
      brands: [],
      options: emptyOptions(),
      existingProducts: [],
      loadingManufacturers: false,
      loadingBrands: false,
      loadingOptions: false,
      saving: false,
      form: {
        manufacturerId: null,
        brandValue: '',
        products: [emptyProduct()]
      },
      rules: {
        manufacturerId: [
          {
            required: true,
            message: 'Manufacturer is required',
            trigger: 'change'
          }
        ],
        brandValue: [
          { required: true, message: 'Brand is required', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    selectedBrand() {
      const name = String(this.form.brandValue || '')
        .trim()
        .toLowerCase()
      return this.brands.find(brand => brand.name.toLowerCase() === name)
    },
    selectedBrandName() {
      return this.selectedBrand
        ? this.selectedBrand.name
        : String(this.form.brandValue || '').trim()
    }
  },
  mounted() {
    this.loadManufacturers()
  },
  methods: {
    responseData(response) {
      const body = response && response.data
      return body && body.data !== undefined ? body.data : body
    },
    async loadManufacturers() {
      this.loadingManufacturers = true
      try {
        const response = await ApiService.GetManufactures()
        const data = this.responseData(response)
        this.manufacturers = Array.isArray(data) ? data : []
      } catch (error) {
        this.$message.error('Failed to load manufacturers')
      } finally {
        this.loadingManufacturers = false
      }
    },
    async onManufacturerChange() {
      this.form.brandValue = ''
      this.brands = []
      this.options = emptyOptions()
      this.existingProducts = []
      this.form.products = [emptyProduct()]
      if (!this.form.manufacturerId) return
      this.loadingBrands = true
      try {
        const response = await ApiService.GetProductBrands(
          this.form.manufacturerId
        )
        const data = this.responseData(response)
        this.brands = (Array.isArray(data) ? data : [])
          .map((brand, index) => {
            const id = this.firstValue(brand, [
              'productBrandID',
              'productBrandId',
              'id',
              'value'
            ])
            const name = this.firstValue(brand, [
              'brandName',
              'productBrandName',
              'name',
              'label'
            ])
            return {
              id,
              name: String(name || ''),
              key: id === undefined || id === null ? `brand-${index}` : id
            }
          })
          .filter(brand => brand.name)
      } catch (error) {
        this.$message.error('Failed to load product brands')
      } finally {
        this.loadingBrands = false
      }
    },
    fetchBrandSuggestions(query, callback) {
      const normalized = String(query || '').toLowerCase()
      callback(
        this.brands.filter(brand =>
          brand.name.toLowerCase().includes(normalized)
        )
      )
    },
    onBrandSelect(brand) {
      this.form.brandValue = brand.name
      this.onBrandChange()
    },
    async onBrandChange() {
      this.options = emptyOptions()
      this.existingProducts = []
      this.form.products = [emptyProduct()]
      if (!this.form.brandValue) return
      this.loadingOptions = true
      try {
        const response = await ApiService.GetProductOptions(
          this.form.manufacturerId,
          this.selectedBrand ? this.selectedBrand.id : undefined
        )
        const data = this.responseData(response) || {}
        this.options = {
          productTypes: this.optionNames(data.productTypes, [
            'typeName',
            'name'
          ]),
          productNames: this.optionNames(data.productNames, [
            'productName',
            'name'
          ]),
          capacities: this.optionNames(data.capacities, [
            'capacityName',
            'name'
          ]),
          absorbencies: this.optionNames(data.absorbencies, [
            'absorbencyName',
            'name'
          ]),
          sizes: this.optionNames(data.sizes, ['sizeName', 'name']),
          colors: this.optionNames(data.colors, ['colorName', 'name'])
        }
        this.existingProducts = Array.isArray(data.existingProducts)
          ? data.existingProducts
          : []
      } catch (error) {
        this.$message.error('Failed to load product options')
      } finally {
        this.loadingOptions = false
      }
    },
    firstValue(item, keys) {
      if (item === null || item === undefined) return undefined
      if (typeof item !== 'object') return item
      for (const key of keys) {
        if (item[key] !== undefined && item[key] !== null) return item[key]
      }
      return undefined
    },
    optionNames(items, keys) {
      const names = (Array.isArray(items) ? items : [])
        .map(item => this.firstValue(item, keys))
        .filter(value => value !== undefined && value !== null && value !== '')
        .map(String)
      return [...new Set(names)]
    },
    requiredRule(label) {
      return [
        { required: true, message: `${label} is required`, trigger: 'change' }
      ]
    },
    addProduct() {
      this.form.products.push(emptyProduct())
    },
    removeProduct(index) {
      this.form.products.splice(index, 1)
    },
    cleanOptional(value) {
      if (value === undefined || value === null || value === '') return null
      if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed || null
      }
      return value
    },
    async saveCatalogue() {
      const valid = await this.$refs.catalogueForm.validate().catch(() => false)
      if (!valid) return
      this.saving = true
      try {
        const payload = {
          manufacturerID: this.form.manufacturerId,
          brandName: this.selectedBrandName,
          products: this.form.products.map(product => ({
            typeName: product.typeName.trim(),
            productName: product.productName.trim(),
            capacityName: this.cleanOptional(product.capacityName),
            absorbencyName: this.cleanOptional(product.absorbencyName),
            sizeName: this.cleanOptional(product.sizeName),
            colorName: this.cleanOptional(product.colorName),
            productNumber: product.productNumber.trim(),
            isoCapacity: this.cleanOptional(product.isoCapacity),
            waistSize: this.cleanOptional(product.waistSize)
          }))
        }
        const response = await ApiService.SaveProductCatalogue(payload)
        if (response.data && response.data.success === 0) {
          throw new Error(response.data.msg || 'Save failed')
        }
        this.$message.success('Product catalogue saved successfully')
        await this.onBrandChange()
      } catch (error) {
        const message =
          (error.response && error.response.data && error.response.data.msg) ||
          error.message ||
          'Failed to save product catalogue'
        this.$message.error(message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.product-catalogue {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.catalogue-card,
.existing-card {
  padding: 22px 24px;
}

.section-heading,
.combinations-header,
.combination-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-heading {
  margin-bottom: 20px;
  h3 {
    margin: 0 0 5px;
    color: #303133;
    font-size: 18px;
  }
  p {
    margin: 0;
    color: #909399;
    font-size: 13px;
  }
  &.compact {
    margin-bottom: 14px;
  }
}

.catalogue-context {
  display: grid;
  grid-template-columns: minmax(240px, 360px) minmax(240px, 360px);
  gap: 18px;
  padding: 18px;
  background: #f7f9fc;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  /deep/ .el-select,
  /deep/ .el-autocomplete {
    width: 100%;
  }
  /deep/ .el-form-item {
    margin-bottom: 0;
  }
  /deep/ .el-form-item__label {
    float: none;
    height: auto;
    padding: 0 0 5px;
    line-height: 20px;
  }
}

.combinations-header {
  margin: 24px 0 12px;
  h4 {
    margin: 0 0 3px;
    color: #303133;
    font-size: 15px;
  }
  span {
    color: #909399;
    font-size: 12px;
  }
}

.combination-card {
  padding: 14px 18px 2px;
  margin-bottom: 12px;
  border: 1px solid #dfe4ec;
  border-left: 3px solid #409eff;
  border-radius: 5px;
  background: #fff;
}

.combination-title {
  margin-bottom: 12px;
  color: #303133;
  font-weight: 600;
}

.remove-button {
  color: #f56c6c;
}

.option-path {
  display: grid;
  grid-template-columns: repeat(6, minmax(135px, 1fr));
  align-items: center;
  gap: 14px;
}

.option-path,
.product-details {
  /deep/ .el-form-item {
    margin-bottom: 12px;
  }
  /deep/ .el-form-item__label {
    float: none;
    height: auto;
    padding: 0 0 4px;
    line-height: 18px;
  }
}

.product-details {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 16px;
  padding-top: 2px;
  border-top: 1px dashed #e4e7ed;
  /deep/ .el-input-number {
    width: 100%;
  }
}

.catalogue-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

@media (max-width: 1500px) {
  .option-path {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
    gap: 0 14px;
  }
}

@media (max-width: 900px) {
  .catalogue-context,
  .option-path,
  .product-details {
    grid-template-columns: 1fr;
  }
}
</style>
